import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { createContext, useCallback, useContext, useEffect, useState, useMemo, ReactNode } from "react";
import { useFeatureFlags } from "./FeatureFlagsProvider";
import { aiCopilotPreviewMessages, countAiCopilotPreviewMessages, refreshAiCopilotPreviewMessages } from "@/redux/features/counter";
import { useChat } from "./ChatProvider";
import { ack, aiCopilotPreview } from "@/redux/features/consent";
import { useUserInformation } from "./UserProvider";
import { Message } from "@/lib/copilot/message";

/**
 * Interface defining the Copilot Preview context values
 */
interface CopilotPreview {
  /** Whether the Copilot preview is enabled for this user */
  enabled: boolean;
  /** Maximum number of messages allowed in preview mode */
  limit: number;
  /** Current count of messages used */
  messageCount: number;
  /** Function to acknowledge the preview message */
  acknowledge: () => void;
  /** Whether the user has acknowledged the preview message */
  acknowledged: boolean;
  /** Function to submit a new message */
  submitMessage: (event?: React.FormEvent<HTMLFormElement>) => Promise<void>;
  /** Date of the oldest message in the preview history */
  oldestMessageDate: Date;
}

const CopilotPreviewContext = createContext<CopilotPreview>(null!);

/**
 * Hook to access the Copilot preview functionality
 */
export const useCopilotPreview = (): CopilotPreview => useContext(CopilotPreviewContext);

/**
 * Time constants
 */
const ONE_DAY_MS = 1000 * 60 * 60 * 24;
const ONE_MIN_MS = 1000 * 60;

/**
 * Provider component that manages the Copilot preview state
 */
export const CopilotPreviewProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const { flags: { FLAG_COPILOT_PREVIEW_LIMIT = 5, FLAG_COPILOT_PREVIEW = false }} = useFeatureFlags();
  const { isLoggedIn } = useUserInformation();
  
  // Calculate timestamp for messages older than 24 hours
  const expiryTimestamp = Date.now() - ONE_DAY_MS;
  
  const { messages, input, addMessage, thread } = useChat();
  
  // Get the preview message timestamps stored in the browser.
  const previewMessages = useAppSelector(aiCopilotPreviewMessages)

  // Store the initial count of preview messages to avoid counting them multiple times
  const [basePreviewMessageCount] = useState(previewMessages
    .filter((timestamp) => timestamp > expiryTimestamp)
    .map((message) => new Date(message))
    .length
  );

  // Count the number of agent messages.
  const agentMessageCount = messages.filter((message) => message.role === 'agent').length;

  // Count the number of messages from the AI in the preview period.
  const previewMessageCount = basePreviewMessageCount + agentMessageCount;
  
  // Determine if the message limit has been reached
  const limitReached = useMemo(() => 
    !isLoggedIn && (previewMessageCount >= FLAG_COPILOT_PREVIEW_LIMIT),
    [isLoggedIn, previewMessageCount, FLAG_COPILOT_PREVIEW_LIMIT]
  );
  
  // Check if the preview message exists in the thread
  const hasLimitMessage = thread.getMessageById('aiCopilotPreview') !== undefined;

  // Remember the last message count to detect when new messages have been added.
  const [currentMessageCount, setCurrentMessageCount] = useState(previewMessageCount);

  // Update the message count in the Redux store when message counts change.
  useEffect(() => {
    if (previewMessageCount > currentMessageCount) {
      const messagesToAdd = previewMessageCount - currentMessageCount;
      for (let i = 0; i < messagesToAdd; i++) {
        dispatch(countAiCopilotPreviewMessages(new Date()));
      }
      setCurrentMessageCount(previewMessageCount);
    }
    dispatch(refreshAiCopilotPreviewMessages(new Date(expiryTimestamp)));
  }, [currentMessageCount, previewMessageCount])

  /**
   * Creates a standardized limit reached status message 
   */
  const setStatusMessage = useCallback(() => {
    addMessage(Message.fromData({
      id: 'aiCopilotPreview',
      role: 'status',
      status: 'completed',
      content: `Thanks for trying Acquia Copilot. Interactions are currently limited to ${FLAG_COPILOT_PREVIEW_LIMIT} messages per day.

We would appreciate your feedback about your experience. Take this [1-minute survey](https://www.surveymonkey.com/r/B6L2W55).`
    }));
  }, [addMessage, FLAG_COPILOT_PREVIEW_LIMIT]);

  /**
   * Effect to update message count and show limit message if needed
   */
  useEffect(() => {
    if (limitReached && !hasLimitMessage) {
      setStatusMessage();
    }
  }, [
    limitReached, 
    hasLimitMessage
  ]);

  /**
   * Handles message submission with limit checking
   */
  const submitMessage = useCallback(async (
    event?: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event?.preventDefault?.();

    if (!input) return;
    
    // Add the user message regardless of limits
    addMessage(Message.fromUserInput(input));
    
    // If preview limit will be reached by this message
    if (FLAG_COPILOT_PREVIEW && !isLoggedIn && limitReached && !hasLimitMessage) {
      setStatusMessage();
    }
  }, [
    input, 
    addMessage, 
    FLAG_COPILOT_PREVIEW, 
    isLoggedIn, 
    limitReached, 
    hasLimitMessage, 
    setStatusMessage
  ]);

  /**
   * Calculate the oldest message date for countdown display
   */
  const oldestMessageDate = useMemo(() => {
    if (previewMessages.length === 0) return new Date();
    return new Date(previewMessages.reduce(
      (oldest, current) => (current < oldest ? current : oldest), 
      previewMessages[0]
    ));
  }, [previewMessages]);

  const acknowledged = useAppSelector(aiCopilotPreview);  

  /**
   * Memoized context value to prevent unnecessary re-renders
   */
  const contextValue = useMemo(() => ({
    enabled: !isLoggedIn && FLAG_COPILOT_PREVIEW,
    limit: FLAG_COPILOT_PREVIEW_LIMIT,
    messageCount: currentMessageCount,
    acknowledged,
    acknowledge: () => dispatch(ack('aiCopilotPreview')),
    submitMessage,
    oldestMessageDate
  }), [
    isLoggedIn, 
    FLAG_COPILOT_PREVIEW, 
    FLAG_COPILOT_PREVIEW_LIMIT, 
    currentMessageCount, 
    acknowledged,
    dispatch, 
    submitMessage, 
    oldestMessageDate
  ]);

  return (
    <CopilotPreviewContext.Provider value={contextValue}>
      {children}
    </CopilotPreviewContext.Provider>
  );
};